import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Activity, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Clock, Wifi, WifiOff, Loader2, ArrowUpRight, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  healthy: { color: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2, label: 'Healthy' },
  degraded: { color: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800', icon: AlertTriangle, label: 'Degraded' },
  unhealthy: { color: 'bg-red-500', badge: 'bg-red-100 text-red-800', icon: XCircle, label: 'Down' },
  not_configured: { color: 'bg-slate-300', badge: 'bg-slate-100 text-slate-600', icon: WifiOff, label: 'Not Configured' },
  unknown: { color: 'bg-slate-300', badge: 'bg-slate-100 text-slate-500', icon: Clock, label: 'Unknown' },
};

const INTEGRATION_NAMES = {
  nvd: 'NVD (NIST)',
  mitre_attack: 'MITRE ATT&CK',
  stripe: 'Stripe Payments',
  email_resend: 'Resend Email',
  nessus: 'Tenable Nessus',
  qualys: 'Qualys Cloud',
  openvas: 'OpenVAS',
};

function HealthCard({ id, data }) {
  const status = STATUS_CONFIG[data?.status] || STATUS_CONFIG.unknown;
  const Icon = status.icon;
  const latency = data?.latency_ms || 0;
  const latencyColor = latency < 200 ? 'text-emerald-600' : latency < 1000 ? 'text-amber-600' : 'text-red-600';

  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`health-card-${id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${status.color} ${data?.status === 'healthy' ? 'animate-pulse' : ''}`} />
            <div>
              <p className="font-medium text-sm text-slate-900">{INTEGRATION_NAMES[id] || id}</p>
              <p className="text-xs text-slate-500 mt-0.5">{data?.message || 'No data'}</p>
            </div>
          </div>
          <Badge className={status.badge}>
            <Icon size={12} className="mr-1" />
            {status.label}
          </Badge>
        </div>

        {data?.status !== 'not_configured' && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock size={12} />
              <span>Latency</span>
            </div>
            <span className={`text-sm font-mono font-medium ${latencyColor}`}>
              {latency}ms
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function IntegrationHealthPage() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchHealth = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.get(`${API}/api/integrations/health/check-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthData(res.data);
      setLastChecked(new Date());
    } catch (err) {
      toast.error('Failed to check integration health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(() => fetchHealth(), 60000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  const summary = healthData?.summary || {};
  const integrations = healthData?.integrations || {};
  const alerts = healthData?.alerts || [];
  const uptimePercent = summary.total > 0
    ? Math.round(((summary.healthy + (summary.not_configured || 0)) / summary.total) * 100)
    : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6 px-1" data-testid="integration-health-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Integration Health</h1>
            <p className="text-sm text-slate-500 mt-1">
              Real-time monitoring of all connected services
              {lastChecked && (
                <span className="ml-2 text-slate-400">
                  &middot; Last checked {lastChecked.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <Button variant="outline" onClick={() => fetchHealth(true)} disabled={refreshing} data-testid="refresh-health-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Healthy', value: summary.healthy, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Degraded', value: summary.degraded, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Down', value: summary.unhealthy, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Not Configured', value: summary.not_configured, color: 'text-slate-500', bg: 'bg-slate-50' },
          ].map((stat) => (
            <Card key={stat.label} className={`border-0 ${stat.bg}`}>
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-bold ${stat.color}`}>{stat.value || 0}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overall Uptime */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Heart size={18} className="text-emerald-500" />
                <span className="font-medium text-slate-900">System Health</span>
              </div>
              <span className="text-2xl font-bold text-slate-900">{uptimePercent}%</span>
            </div>
            <Progress value={uptimePercent} className="h-2" />
            <p className="text-xs text-slate-500 mt-2">
              {summary.healthy} of {summary.total - (summary.not_configured || 0)} configured integrations are healthy
            </p>
          </CardContent>
        </Card>

        {/* Alerts */}
        {alerts.length > 0 && (
          <Card className="border-red-200 bg-red-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-red-800 flex items-center gap-2">
                <AlertTriangle size={18} />
                Active Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.map((alert, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white rounded-md border border-red-100">
                    <div className="flex items-center gap-2">
                      <XCircle size={14} className={alert.severity === 'critical' ? 'text-red-500' : 'text-amber-500'} />
                      <span className="text-sm font-medium">{INTEGRATION_NAMES[alert.integration] || alert.integration}</span>
                    </div>
                    <span className="text-xs text-slate-500">{alert.message}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Integration Grid */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">All Integrations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(integrations).map(([id, data]) => (
              <HealthCard key={id} id={id} data={data} />
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
